// src/shared/components/ui/input.tsx

import type { InputHTMLAttributes, ReactNode } from 'react'

import { cn } from '@/shared/utils/cn'

type InputSize = 'sm' | 'md' | 'lg'

const inputSizeMap: Record<InputSize, string> = {
  sm: 'h-8 text-xs',
  md: 'h-9 text-sm',
  lg: 'h-10 text-sm',
}

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: InputSize
  invalid?: boolean
  iconLeft?: ReactNode
  addonRight?: ReactNode
}

export function Input({
  className,
  inputSize = 'md',
  invalid,
  iconLeft,
  addonRight,
  ...props
}: InputProps) {
  if (!iconLeft && !addonRight) {
    return (
      <input
        aria-invalid={invalid || undefined}
        className={cn(
          'w-full rounded-md border bg-[var(--panel)] px-3 text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50',
          inputSizeMap[inputSize],
          invalid
            ? 'border-[var(--color-danger)] focus-visible:ring-[var(--color-danger)]'
            : 'border-[var(--panel-border)] focus-visible:ring-[var(--color-brand)] focus-visible:border-[var(--color-brand)]',
          className,
        )}
        {...props}
      />
    )
  }

  return (
    <div
      aria-invalid={invalid || undefined}
      className={cn(
        'flex w-full items-center gap-2 rounded-md border bg-[var(--panel)] px-3 transition focus-within:outline-none focus-within:ring-2 disabled:cursor-not-allowed',
        inputSizeMap[inputSize],
        invalid
          ? 'border-[var(--color-danger)] focus-within:ring-[var(--color-danger)]'
          : 'border-[var(--panel-border)] focus-within:ring-[var(--color-brand)] focus-within:border-[var(--color-brand)]',
        className,
      )}
    >
      {iconLeft ? (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-[var(--color-text-muted)]">
          {iconLeft}
        </span>
      ) : null}
      <input
        className="h-full min-w-0 flex-1 bg-transparent text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        {...props}
      />
      {addonRight ? <span className="shrink-0">{addonRight}</span> : null}
    </div>
  )
}
